/**
 * Mesa "Sobrepor & alinhar" (E29.F2/ADR-0040 §A/§B/§D). // ux-neutrality:allow doc-comment
 *
 * Canvas Fabric.js v6 (padrão de AnalysisCanvas) com o PADRÃO como base // ux-neutrality:allow doc-comment
 * read-only (`selectable=false, evented=false`) e a CONTESTADA como objeto // ux-neutrality:allow doc-comment
 * manipulável no MESMO sistema de coordenadas do canvas — corrige o // ux-neutrality:allow doc-comment
 * letterboxing independente de dois <img> object-contain. A opacidade é a // ux-neutrality:allow doc-comment
 * `opacity` REAL do objeto Fabric (sem fundo de papel opaco lavando a base). // ux-neutrality:allow doc-comment
 * // ux-neutrality:allow doc-comment
 * Gestos nativos do Fabric: arrastar (mover), escalar (handles de canto) e // ux-neutrality:allow doc-comment
 * rotacionar (handle de rotação). O canvas é a fonte da verdade enquanto // ux-neutrality:allow doc-comment
 * montado; o `transform` recebido apenas SEMEIA o estado inicial (persistência // ux-neutrality:allow doc-comment
 * ao alternar Comparar↔Mesa vem do pai). Como o canvas não dá DOM assertável, // ux-neutrality:allow doc-comment
 * o transform corrente é exposto em `data-transform` (gotcha ADR-0040 §3). // ux-neutrality:allow doc-comment
 * // ux-neutrality:allow doc-comment
 * Defensibilidade (ADR-0040 §D / ADR-0026): manipulação é apresentação-only e // ux-neutrality:allow doc-comment
 * efêmera — nada do transform vira medida, motor ou laudo. // ux-neutrality:allow doc-comment
 */
import { useEffect, useRef, useState } from 'react';
import * as fabric from 'fabric';
import { ptBR } from '@/i18n/pt-BR';
import { TOKEN_HEX } from '@/lib/design_tokens';
import type { MesaAnnotationData } from '@/lib/mesa_annotations';
import { wireOverlapAnnotations, type OverlapAnnotationsHandle } from './OverlapAnnotations';
import { wireOverlapViewport, type OverlapViewportHandle } from './OverlapViewport';
import { clampZoom, WHEEL_ZOOM_STEP, zoomAtPoint, type ZoomCommand } from './ZoomPanViewport';

export interface OverlapTransform {
  x: number;
  y: number;
  scale: number;
  angle: number;
  opacity: number;
}

export const OVERLAP_IDENTITY: OverlapTransform = {
  x: 0,
  y: 0,
  scale: 1,
  angle: 0,
  opacity: 0.6,
};

export interface OverlapCanvasProps {
  patternSrc: string | null;
  contestedSrc: string | null;
  transform: OverlapTransform;
  onTransformChange: (next: OverlapTransform) => void;
  /** E36.F2: comando de zoom da toolbar (caber/100%) — só visualização. */
  viewCommand?: ZoomCommand | null;
  /** E36.F3: anotações manuais persistidas, exibidas ancoradas à contestada
   * (read-only na mesa — desenhar/editar é do modo Comparar). */
  annotations?: MesaAnnotationData[];
}

// E37.F3.T01: a mesa dimensiona-se pela área de trabalho real (largura da
// coluna central × altura disponível, medidas por ResizeObserver). Estes são só
// o FALLBACK quando o stage ainda não tem layout (jsdom dos testes, primeiro
// paint antes da medição) — em produção o `baseScale` vem da área medida.
const FALLBACK_WIDTH = 560;
const FALLBACK_HEIGHT = 360;
// Padding do `.overlap-stage` (p-2 = 8px de cada lado) descontado da medição
// síncrona via clientWidth/Height (que inclui padding); o ResizeObserver já
// entrega o content-box (sem padding).
const STAGE_INSET = 8;
// Teto de upscale do padrão para preencher telas grandes sem escala patológica
// (a lente de zoom cobre a magnificação fina por cima).
const MAX_BASE_SCALE = 6;
// E29.F2.C01 (F-E29.F2-01): margem ao redor das imagens para que os handles do
// Fabric (canto = escala; mtr = rotação, ~40px ACIMA da borda superior) caiam
// DENTRO do canvas e fiquem clicáveis pelo smoke. O transform (x,y) é relativo
// ao padrão (offset), então identidade segue (0,0) mesmo com as imagens
// deslocadas em `PAD`. A geometria de identidade é determinística e o smoke a
// reproduz a partir de `PAD` + tamanho do canvas.
export const OVERLAP_PAD = 56;

/** baseScale que faz o padrão (natW×natH) caber na área de trabalho disponível
 * (descontando PAD), com upscale limitado. Substitui o 560×360 fixo (E37.F3). */
function computeBaseScale(natW: number, natH: number, availW: number, availH: number): number {
  const fit = Math.min((availW - 2 * OVERLAP_PAD) / natW, (availH - 2 * OVERLAP_PAD) / natH);
  if (!Number.isFinite(fit) || fit <= 0) return 1;
  return Math.min(fit, MAX_BASE_SCALE);
}

/** Área de trabalho (content-box) do stage para a medição síncrona inicial;
 * fallback quando o stage ainda não tem layout (jsdom/primeiro paint). */
function stageContentSize(stage: HTMLElement | null): { w: number; h: number } {
  const w = (stage?.clientWidth ?? 0) - 2 * STAGE_INSET;
  const h = (stage?.clientHeight ?? 0) - 2 * STAGE_INSET;
  return { w: w > 0 ? w : FALLBACK_WIDTH, h: h > 0 ? h : FALLBACK_HEIGHT };
}
// E29.F3: o handle de rotação (mtr) fica este tanto ACIMA da borda superior —
// mesmo valor default do Fabric, preservado para o smoke continuar acertando-o.
const ROTATE_OFFSET = 40;

// E29.F3: ícone de rotação (Lucide "rotate-cw"), branco, desenhado no handle
// mtr em vez do quadrado default — mais intuitivo. Carregado lazy (sem
// side-effect no import; o módulo é importado no jsdom dos testes).
const ROTATE_ICON_SVG =
  '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" ' +
  `stroke="${TOKEN_HEX.bg}" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round">` +
  '<path d="M21 12a9 9 0 1 1-2.64-6.36"/><path d="M21 3v6h-6"/></svg>';
let rotateIconCache: HTMLImageElement | null = null;
function getRotateIcon(): HTMLImageElement {
  if (rotateIconCache === null) {
    rotateIconCache = new Image();
    rotateIconCache.src = 'data:image/svg+xml;utf8,' + encodeURIComponent(ROTATE_ICON_SVG);
  }
  return rotateIconCache;
}

/** Desenha o controle de rotação: disco de aço + ícone de rotação claro. */
function renderRotateControl(ctx: CanvasRenderingContext2D, left: number, top: number): void {
  const disc = 22;
  const icon = getRotateIcon();
  ctx.save();
  ctx.beginPath();
  ctx.arc(left, top, disc / 2, 0, 2 * Math.PI);
  // E54.F5.T02: o instrumento é de aço, como todo controle do sistema.
  ctx.fillStyle = TOKEN_HEX.accent700;
  ctx.fill();
  ctx.lineWidth = 1;
  ctx.strokeStyle = TOKEN_HEX.bg;
  ctx.stroke();
  if (icon.complete && icon.naturalWidth > 0) {
    const s = 13;
    ctx.drawImage(icon, left - s / 2, top - s / 2, s, s);
  }
  ctx.restore();
}

/**
 * Troca o quadrado default do handle de rotação da contestada por um ícone de
 * rotação (E29.F3). Reusa os handlers de rotação do Fabric (sem reimplementar
 * gesto) e o offset default — só o desenho muda. Guard: no jsdom dos testes o
 * Fabric é mockado e não expõe `Control`/`controlsUtils`, então é no-op lá.
 */
function customizeRotationControl(obj: fabric.FabricImage, onIconLoad: () => void): void {
  if (typeof fabric.Control !== 'function' || !fabric.controlsUtils?.rotationWithSnapping) {
    return;
  }
  obj.controls = {
    ...obj.controls,
    mtr: new fabric.Control({
      x: 0,
      y: -0.5,
      offsetY: -ROTATE_OFFSET,
      actionHandler: fabric.controlsUtils.rotationWithSnapping,
      cursorStyleHandler: fabric.controlsUtils.rotationStyleHandler,
      actionName: 'rotate',
      render: renderRotateControl,
    }),
  };
  obj.setCoords?.();
  const icon = getRotateIcon();
  if (!icon.complete) icon.addEventListener('load', onIconLoad, { once: true });
}

/** Lente resultante de um comando da toolbar/atalhos sobre a mesa: "caber" =
 * identidade (canvas já dimensionado p/ caber); "100%"/presets = padrão em
 * escala relativa à natural (ratio/baseScale) ancorado no canto do padrão
 * (PAD visível); in/out = um tique de roda no centro do canvas (E37.F1.T02 —
 * mesma matemática do ZoomPanViewport). */
function viewForCommand(
  command: ZoomCommand,
  canvasEl: HTMLCanvasElement | null,
  baseScale: number,
  current: { zoom: number; panX: number; panY: number },
): { zoom: number; panX: number; panY: number } {
  if (command.action === 'fit') return { zoom: 1, panX: 0, panY: 0 };
  if (command.action === 'in' || command.action === 'out') {
    const factor = command.action === 'in' ? WHEEL_ZOOM_STEP : 1 / WHEEL_ZOOM_STEP;
    return zoomAtPoint(
      current,
      factor,
      (canvasEl?.clientWidth ?? 0) / 2,
      (canvasEl?.clientHeight ?? 0) / 2,
    );
  }
  const ratio = command.action === 'percent' ? (command.percent ?? 100) / 100 : 1;
  const zoom = clampZoom(ratio / (baseScale || 1));
  return { zoom, panX: OVERLAP_PAD * (1 - zoom), panY: OVERLAP_PAD * (1 - zoom) };
}

/** Arredonda para reduzir ruído no data-attribute lido pelo smoke. */
function roundTransform(t: OverlapTransform): OverlapTransform {
  return {
    x: Math.round(t.x * 100) / 100,
    y: Math.round(t.y * 100) / 100,
    scale: Math.round(t.scale * 1000) / 1000,
    angle: Math.round(t.angle * 100) / 100,
    opacity: Math.round(t.opacity * 1000) / 1000,
  };
}

export function OverlapCanvas({
  patternSrc,
  contestedSrc,
  transform,
  onTransformChange,
  viewCommand = null,
  annotations = [],
}: OverlapCanvasProps): JSX.Element {
  const canvasElRef = useRef<HTMLCanvasElement | null>(null);
  // E37.F3.T01: stage observado por ResizeObserver — a mesa re-ajusta o
  // baseScale à área de trabalho real sem tocar o transform persistido.
  const stageRef = useRef<HTMLDivElement | null>(null);
  const fabricRef = useRef<fabric.Canvas | null>(null);
  const contestedRef = useRef<fabric.FabricImage | null>(null);
  // Padrão base: reposicionado no re-fit (escala junto com a área).
  const patternRef = useRef<fabric.FabricImage | null>(null);
  // Dimensões naturais do padrão (origem do baseScale a cada re-fit).
  const natRef = useRef<{ w: number; h: number } | null>(null);
  const baseScaleRef = useRef(1);
  // Posição (canvas coords) do padrão = origem do sistema do transform.
  const offsetRef = useRef({ x: OVERLAP_PAD, y: OVERLAP_PAD });
  // E36.F2: lente de visualização (viewportTransform) — nunca toca a contestada.
  const viewportRef = useRef<OverlapViewportHandle | null>(null);
  const [viewZoom, setViewZoom] = useState(1);
  // E37.F1: baseScale espelhado em state p/ o badge de % (100% = escala
  // natural do padrão; viewZoom * baseScale é o fator de render efetivo).
  const [stageScale, setStageScale] = useState(1);
  // E36.F3: anotações ancoradas à contestada + vista serializada (asserts).
  const annotationsHandleRef = useRef<OverlapAnnotationsHandle | null>(null);
  const [annotationsView, setAnnotationsView] = useState('[]');

  // Refs lidas pelo effect a cada mount: evitam re-montar o canvas a cada
  // edição (canvas é a fonte da verdade enquanto montado; pai persiste).
  const initialTransformRef = useRef(transform);
  initialTransformRef.current = transform;
  const onChangeRef = useRef(onTransformChange);
  onChangeRef.current = onTransformChange;
  const annotationsRef = useRef(annotations);
  annotationsRef.current = annotations;

  // Espelho local para o data-attribute e o valor do slider.
  const [current, setCurrent] = useState<OverlapTransform>(transform);

  function publish(next: OverlapTransform): void {
    const rounded = roundTransform(next);
    setCurrent(rounded);
    onChangeRef.current(rounded);
  }

  useEffect(() => {
    if (canvasElRef.current === null || patternSrc === null || contestedSrc === null) {
      return undefined;
    }
    const canvasEl = canvasElRef.current;
    const fabricCanvas = new fabric.Canvas(canvasEl, {
      selection: false,
      renderOnAddRemove: true,
      // E54.F5.T02: a mesa é a prancha do sistema, não um branco arbitrário.
      backgroundColor: TOKEN_HEX.paper,
    });
    fabricRef.current = fabricCanvas;

    let cancelled = false;

    function readContested(): OverlapTransform {
      const obj = contestedRef.current;
      const baseScale = baseScaleRef.current || 1;
      const offset = offsetRef.current;
      if (obj === null) return initialTransformRef.current;
      return {
        x: (obj.left ?? offset.x) - offset.x,
        y: (obj.top ?? offset.y) - offset.y,
        scale: (obj.scaleX ?? baseScale) / baseScale,
        angle: obj.angle ?? 0,
        opacity: obj.opacity ?? OVERLAP_IDENTITY.opacity,
      };
    }

    function emit(): void {
      publish(readContested());
    }

    /**
     * E37.F3.T01: re-ajusta o baseScale à área de trabalho medida e reposiciona
     * padrão + contestada a partir do transform RELATIVO corrente. Não publica:
     * `readContested()` (baseScale antigo) → mesmas coordenadas relativas com o
     * baseScale novo, logo o `data-transform` persistido NÃO muda. PAD constante
     * mantém os handles do Fabric clicáveis (lição E29.F2.C01).
     */
    function refitTo(availW: number, availH: number): void {
      const nat = natRef.current;
      const obj = contestedRef.current;
      const pat = patternRef.current;
      if (nat === null || obj === null || pat === null) return;
      const baseScale = computeBaseScale(nat.w, nat.h, availW, availH);
      if (Math.abs(baseScale - baseScaleRef.current) < 1e-3) return;
      const rel = readContested();
      baseScaleRef.current = baseScale;
      setStageScale(baseScale);
      fabricCanvas.setDimensions({
        width: nat.w * baseScale + 2 * OVERLAP_PAD,
        height: nat.h * baseScale + 2 * OVERLAP_PAD,
      });
      pat.set({ left: OVERLAP_PAD, top: OVERLAP_PAD, scaleX: baseScale, scaleY: baseScale });
      obj.set({
        left: OVERLAP_PAD + rel.x,
        top: OVERLAP_PAD + rel.y,
        scaleX: baseScale * rel.scale,
        scaleY: baseScale * rel.scale,
        angle: rel.angle,
        opacity: rel.opacity,
      });
      obj.setCoords?.();
      fabricCanvas.requestRenderAll();
      annotationsHandleRef.current?.sync();
    }

    void (async () => {
      try {
        const [pattern, contested] = await Promise.all([
          fabric.FabricImage.fromURL(patternSrc, { crossOrigin: 'anonymous' }),
          fabric.FabricImage.fromURL(contestedSrc, { crossOrigin: 'anonymous' }),
        ]);
        if (cancelled || fabricRef.current === null) return;

        const natW = pattern.width ?? 1;
        const natH = pattern.height ?? 1;
        natRef.current = { w: natW, h: natH };
        // E37.F3.T01: baseScale pela área de trabalho medida (não mais 560×360).
        const avail = stageContentSize(stageRef.current);
        const baseScale = computeBaseScale(natW, natH, avail.w, avail.h);
        baseScaleRef.current = baseScale;
        setStageScale(baseScale);
        offsetRef.current = { x: OVERLAP_PAD, y: OVERLAP_PAD };
        fabricCanvas.setDimensions({
          width: natW * baseScale + 2 * OVERLAP_PAD,
          height: natH * baseScale + 2 * OVERLAP_PAD,
        });

        pattern.set({
          left: OVERLAP_PAD,
          top: OVERLAP_PAD,
          scaleX: baseScale,
          scaleY: baseScale,
          selectable: false,
          evented: false,
        });
        fabricCanvas.add(pattern);
        fabricCanvas.sendObjectToBack(pattern);
        patternRef.current = pattern;

        const seed = initialTransformRef.current;
        contested.set({
          left: OVERLAP_PAD + seed.x,
          top: OVERLAP_PAD + seed.y,
          scaleX: baseScale * seed.scale,
          scaleY: baseScale * seed.scale,
          angle: seed.angle,
          opacity: seed.opacity,
          selectable: true,
          evented: true,
          // E54.F5.T02: a moldura de manipulação em aço, com alça quadrada de
          // contorno — o azul default do Fabric era a última cor da mesa fora
          // do sistema. `cornerStyle` fica no default ('rect'): canto reto.
          borderColor: TOKEN_HEX.accent700,
          cornerColor: TOKEN_HEX.paper,
          cornerStrokeColor: TOKEN_HEX.accent700,
          transparentCorners: false,
          cornerSize: 10,
        });
        contestedRef.current = contested;
        // E29.F3: ícone de rotação no handle mtr (em vez do quadrado).
        customizeRotationControl(contested, () => fabricRef.current?.requestRenderAll());
        fabricCanvas.add(contested);
        fabricCanvas.setActiveObject(contested);
        fabricCanvas.requestRenderAll();
        // E36.F3: a contestada existe — projeta as anotações sobre ela.
        annotationsHandleRef.current?.setAnnotations(annotationsRef.current);
      } catch {
        // Falha silenciosa — fallback é o canvas vazio + controles.
      }
    })();

    fabricCanvas.on('object:modified', emit);
    // E36.F2/T02: zoom (roda) e pan (espaço/botão-do-meio) da visualização.
    const viewport = wireOverlapViewport(fabricCanvas, setViewZoom);
    viewportRef.current = viewport;
    // E36.F3: anotações ancoradas ao transform da contestada (read-only).
    const annotationsHandle = wireOverlapAnnotations(
      fabricCanvas,
      () => contestedRef.current,
      setAnnotationsView,
    );
    annotationsHandleRef.current = annotationsHandle;

    // E37.F3.T01: re-fit ao redimensionar a coluna central / a janela. O
    // content-box (sem padding) vem do entry; medições degeneradas (jsdom,
    // sem altura) são ignoradas — o re-fit só roda com área real.
    const resizeObserver =
      typeof ResizeObserver === 'function'
        ? new ResizeObserver((entries) => {
            const rect = entries[0]?.contentRect;
            if (rect === undefined || !(rect.width > 0) || !(rect.height > 0)) return;
            refitTo(rect.width, rect.height);
          })
        : null;
    if (resizeObserver !== null && stageRef.current !== null) {
      resizeObserver.observe(stageRef.current);
    }

    return () => {
      cancelled = true;
      fabricRef.current = null;
      contestedRef.current = null;
      patternRef.current = null;
      natRef.current = null;
      viewportRef.current = null;
      annotationsHandleRef.current = null;
      resizeObserver?.disconnect();
      annotationsHandle.dispose();
      viewport.dispose();
      fabricCanvas.off('object:modified', emit);
      fabricCanvas.dispose();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patternSrc, contestedSrc]);

  // E36.F3: lista mudou (carga inicial, mover/apagar no Comparar) — reprojeta.
  useEffect(() => {
    annotationsHandleRef.current?.setAnnotations(annotations);
  }, [annotations]);

  // Comando da toolbar/atalhos aplicado à lente da mesa (ver viewForCommand).
  useEffect(() => {
    const viewport = viewportRef.current;
    if (viewCommand === null || viewport === null) return;
    viewport.set(
      viewForCommand(viewCommand, canvasElRef.current, baseScaleRef.current, viewport.get()),
    );
  }, [viewCommand]);

  function handleOpacity(value: number): void {
    const next = { ...current, opacity: value };
    const obj = contestedRef.current;
    if (obj !== null) {
      obj.set({ opacity: value });
      fabricRef.current?.requestRenderAll();
    }
    publish(next);
  }

  function handleReset(): void {
    const obj = contestedRef.current;
    const baseScale = baseScaleRef.current || 1;
    const offset = offsetRef.current;
    if (obj !== null) {
      obj.set({
        left: offset.x + OVERLAP_IDENTITY.x,
        top: offset.y + OVERLAP_IDENTITY.y,
        scaleX: baseScale * OVERLAP_IDENTITY.scale,
        scaleY: baseScale * OVERLAP_IDENTITY.scale,
        angle: OVERLAP_IDENTITY.angle,
        opacity: OVERLAP_IDENTITY.opacity,
      });
      // setCoords: recalcula oCoords (hit-test dos handles) imediatamente, para
      // o próximo gesto interativo acertar canto/mtr na geometria de identidade.
      obj.setCoords?.();
      fabricRef.current?.requestRenderAll();
      // E36.F3: Resetar não dispara object:modified — reprojeta as anotações.
      annotationsHandleRef.current?.sync();
    }
    publish({ ...OVERLAP_IDENTITY });
  }

  return (
    <div
      className="overlap-canvas"
      data-testid="overlap-canvas"
      data-transform={JSON.stringify(current)}
      data-view-zoom={viewZoom}
      data-annotations={annotationsView}
    >
      <div className="overlap-stage" ref={stageRef}>
        <canvas ref={canvasElRef} className="block max-h-full max-w-full" />
        {/* E37.F1: % relativo à escala natural do padrão (coerente com os
            presets da toolbar e o badge do Comparar). */}
        <span className="zoompan-badge" data-testid="overlap-zoom-badge">
          {Math.round(viewZoom * stageScale * 100)}%
        </span>
      </div>
      <div className="overlap-controls">
        <label className="overlap-opacity-label">
          {ptBR.workspaceCaso.overlapOpacityLabel}
          <input
            type="range"
            min={0}
            max={100}
            value={Math.round(current.opacity * 100)}
            onChange={(e) => handleOpacity(Number(e.target.value) / 100)}
            aria-label={ptBR.workspaceCaso.overlapOpacityLabel}
            data-testid="overlap-opacity"
          />
          <span className="overlap-opacity-value">{Math.round(current.opacity * 100)}%</span>
        </label>
        <button type="button" data-testid="overlap-reset" onClick={handleReset}>
          {ptBR.workspaceCaso.overlapReset}
        </button>
      </div>
      <p className="overlap-note" data-testid="overlap-note">
        {ptBR.workspaceCaso.overlapNote}
      </p>
    </div>
  );
}
